import { useState } from 'react'
import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from './locales/en.json'
import ar from './locales/ar.json'
import fr from './locales/fr.json'

export type Locale = 'en' | 'ar' | 'fr'

const ALLOWED_LOCALES: readonly Locale[] = ['en', 'ar', 'fr']
const RTL_LOCALES = new Set<Locale>(['ar'])

/**
 * Determines the active locale.
 * Priority: ?lang param → navigator.language prefix → 'en' fallback.
 * Unknown / unsupported codes are silently skipped.
 */
export function resolveLocale(langParam?: string | null, navLanguage?: string): Locale {
	if (langParam) {
		const lower = langParam.toLowerCase()
		if ((ALLOWED_LOCALES as readonly string[]).includes(lower)) return lower as Locale
	}
	if (navLanguage) {
		const prefix = navLanguage.split('-')[0].toLowerCase()
		if ((ALLOWED_LOCALES as readonly string[]).includes(prefix)) return prefix as Locale
	}
	return 'en'
}

/**
 * Initialises i18next and ALWAYS awaits the result before returning.
 * Must be awaited in main.tsx before ReactDOM.createRoot().render() is called.
 *
 * When VITE_LOCALIZATION_ENABLED is off: initialises with English-only resources
 * so every useTranslation() call site is safe (no Suspense throws, no blank pages).
 *
 * When VITE_LOCALIZATION_ENABLED is on: initialises with all locales, resolves
 * the active locale from ?lang param → navigator.language → 'en', and sets
 * document.dir for RTL.
 */
export async function initI18n(): Promise<void> {
	// Prevent double-init (e.g. test re-runs, edge-case hot reloads)
	if (i18next.isInitialized) return

	const flag = import.meta.env.VITE_LOCALIZATION_ENABLED
	const localizationEnabled = Boolean(flag) && flag !== 'false'

	if (!localizationEnabled) {
		// Always initialise — even flag-off — so useTranslation() is never
		// called on an uninitialised instance (which throws a Promise in
		// react-i18next with useSuspense:true and crashes the whole tree).
		await i18next.use(initReactI18next).init({
			lng: 'en',
			resources: { en: { translation: en } },
			interpolation: { escapeValue: false },
			react: { useSuspense: false },
		})
		return
	}

	const params = new URLSearchParams(window.location.search)
	const locale = resolveLocale(params.get('lang'), navigator.language)

	document.documentElement.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr'

	await i18next.use(initReactI18next).init({
		lng: locale,
		fallbackLng: 'en',
		resources: {
			en: { translation: en },
			ar: { translation: ar },
			fr: { translation: fr },
		},
		interpolation: { escapeValue: false },
		react: { useSuspense: false },
	})
}

/** The shared i18next instance. */
export { i18next as i18nInstance }

/**
 * Hook: returns the current locale and a setter that updates document.dir
 * and calls i18next.changeLanguage(). Safe to call only when i18next is
 * initialised (i.e. inside components rendered after initI18n() resolves).
 */
export function useLocale(): { locale: Locale; setLocale: (locale: Locale) => void } {
	const [locale, setLocaleState] = useState<Locale>(
		() => (i18next.resolvedLanguage as Locale | undefined) ?? 'en',
	)

	const setLocale = (next: Locale): void => {
		void i18next.changeLanguage(next).then(() => {
			document.documentElement.dir = RTL_LOCALES.has(next) ? 'rtl' : 'ltr'
			setLocaleState(next)
		})
	}

	return { locale, setLocale }
}
